import React, { useState, memo, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Drawer,
  TextInput,
  Textarea,
  Button,
  Modal,
  Select,
  MultiSelect,
  Group,
  Box,
  ActionIcon,
  Badge,
  Title,
} from "@mantine/core";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  IconArrowRight,
  IconArrowUp,
  IconGripVertical,
  IconTrash,
} from "@tabler/icons-react";
import fetchApi from "../components/Lib/api";
import useStartupStore from "../stores/startupStore";
import { IconArrowDown } from "@tabler/icons-react";
import Mixpanel from "../components/Lib/mixpanel";

/* ====================
   GLOBAL STYLES & HELPERS
==================== */

const priorityStyles = {
  Low: {
    backgroundColor: "#d4edda",
    color: "#155724",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "inline-block",
    fontSize: "12px",
    marginTop: "4px",
  },
  Medium: {
    backgroundColor: "#fff3cd",
    color: "#856404",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "inline-block",
    fontSize: "12px",
    marginTop: "4px",
  },
  High: {
    backgroundColor: "#f8d7da",
    color: "#721c24",
    padding: "4px 8px",
    borderRadius: "4px",
    display: "inline-block",
    fontSize: "12px",
    marginTop: "4px",
  },
};

const tagStyle = (color) => ({
  backgroundColor: color,
  color: "#fff",
  padding: "2px 6px",
  borderRadius: "4px",
  fontSize: "10px",
  marginRight: "4px",
  marginTop: "2px",
});

/* ====================
   TABLE VIEW COMPONENT
==================== */
const TableView = ({
  columns,
  columnOrder,
  onTaskClick,
  onAddTask,
  boardTitle,
  boardDescription,
  isSubmitted
}) => {
  const tableContainerStyle = {};

  const headerStyle = {
    paddingBottom: "8px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
  };

  const thStyle = {
    borderBottom: "2px solid #ddd",
    padding: "12px",
    textAlign: "left",
    backgroundColor: "#f9f9f9",
  };

  const tdStyle = {
    borderBottom: "1px solid #eee",
    padding: "12px",
  };

  // Flatten tasks from all columns and include extra date fields.
  const tasks = [];
  columnOrder.forEach((colId) => {
    const column = columns[colId];
    column.tasks.forEach((task, idx) => {
      tasks.push({
        ...task,
        columnId: colId,
        index: idx,
        columnName: column.name,
        columnColor: column.color,
      });
    });
  });

  return (
    <div style={tableContainerStyle}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Title</th>
            <th style={thStyle}>Description</th>
            {/* <th style={thStyle}>Start Date</th>
            <th style={thStyle}>End Date</th> */}
            <th style={thStyle}>Priority</th>
            <th style={thStyle}>Column</th>
            {/* <th style={thStyle}>Files</th> */}
            <th style={thStyle}>Assignees</th>
            <th style={thStyle}>Tags</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => (
            <tr
              key={task.id}
              style={{ cursor: "pointer" }}
              onClick={() => onTaskClick(task.columnId, task.index, task)}
            >
              <td style={tdStyle}>{task.title}</td>
              <td style={tdStyle}>{task.description}</td>
              {/* <td style={tdStyle}>{task.start_date || "-"}</td>
              <td style={tdStyle}>{task.end_date || "-"}</td> */}
              <td style={tdStyle}>
                <span style={priorityStyles[task.priority] || {}}>
                  {task.priority}
                </span>
              </td>
              <td style={tdStyle}>
                <span
                  style={{
                    display: "inline-block",
                    width: "12px",
                    height: "12px",
                    backgroundColor: task.columnColor,
                    borderRadius: "50%",
                    marginRight: "6px",
                  }}
                ></span>
                {task.columnName}
              </td>
              {/* <td style={tdStyle}>
                {task.files && task.files.length > 0
                  ? task.files.join(", ")
                  : "-"}
              </td> */}
              <td style={tdStyle}>
                <Group spacing="xs" mt={4}>
                  {task.assignees &&
                    task.assignees.map((assignee) => (
                      <Badge key={assignee.id} color="blue" variant="light">
                        {assignee.first_name} {assignee.last_name}
                      </Badge>
                    ))}
                </Group>
              </td>
              <td style={tdStyle}>
                {task.tags &&
                  task.tags.map((tag, idx) => (
                    <span key={idx} style={tagStyle(tag.color)}>
                      {tag.name}
                    </span>
                  ))}
              </td>
            </tr>
          ))}
          {/* Last row allows user to add a new task */}
          <tr
            onClick={onAddTask}
            style={{
              cursor: "pointer",
              backgroundColor: "#f9f9f9",
            }}
          >
          {!isSubmitted && (
            <td colSpan="9" style={{ textAlign: "center", padding: "12px" }}>
              + Add New Task
            </td>
          )}  
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/* ====================
   TASK MODAL COMPONENT
==================== */
const TaskModal = memo(function TaskDrawer({
  taskData,
  onChange,
  onSave,
  onDelete,
  onClose,
  onFileChange,
  colors,
  users,
  columns,
}) {
  const tagInputRef = useRef(null);
  const stopKey = (e) => e.stopPropagation();

  return (
    <Drawer
      opened={true}
      onClose={onClose}
      title={taskData.mode === "add" ? "Add Task" : "Edit Task"}
      padding="xl"
      size="xl"
      position="right"
    >
      <Box p="md">
        <TextInput
          placeholder="Title"
          value={taskData.task.title}
          onChange={(e) => onChange({ field: "title", value: e.target.value })}
          autoFocus
          onKeyDown={stopKey}
          mb="md"
          size="lg"
          styles={{
            input: {
              fontSize: "1.1rem",
              padding: "1rem",
            },
          }}
        />

        <Textarea
          placeholder="Description"
          value={taskData.task.description}
          onChange={(e) =>
            onChange({ field: "description", value: e.target.value })
          }
          onKeyDown={stopKey}
          minRows={5}
          mb="md"
          size="lg"
          styles={{
            input: {
              fontSize: "1.1rem",
              padding: "1rem",
            },
          }}
        />

        <Select
          data={[
            { value: "Low", label: "Low" },
            { value: "Medium", label: "Medium" },
            { value: "High", label: "High" },
          ]}
          value={taskData.task.priority}
          onChange={(value) => onChange({ field: "priority", value })}
          onKeyDown={stopKey}
          placeholder="Select Priority"
          mb="md"
          size="lg"
          styles={{
            input: {
              fontSize: "1.1rem",
              padding: "1rem",
            },
          }}
        />

        <Select
          data={Object.keys(columns).map((colId) => ({
            value: colId,
            label: columns[colId].name,
          }))}
          value={taskData.columnId || ""}
          onChange={(value) => onChange({ field: "columnId", value })}
          onKeyDown={stopKey}
          placeholder="Select Column"
          mb="md"
          size="lg"
          styles={{
            input: {
              fontSize: "1.1rem",
              padding: "1rem",
            },
          }}
        />

        <MultiSelect
          label="Assignees"
          data={users.map((user) => ({
            value: user.id,
            label: user.first_name + " " + user.last_name,
          }))}
          value={(taskData.task.assignees || []).map((a) => a.id)}
          onChange={(selectedIds) => {
            const selected = users.filter((u) => selectedIds.includes(u.id));
            onChange({ field: "assignees", value: selected });
          }}
          onKeyDown={stopKey}
          placeholder="Select Assignees"
          mb="md"
          size="lg"
          styles={{
            input: {
              fontSize: "1.1rem",
              padding: "1rem",
            },
            label: {
              fontSize: "1rem",
              marginBottom: "0.5rem",
            },
          }}
        />

        <TextInput
          ref={tagInputRef}
          placeholder="Add a tag and press Enter"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.value.trim()) {
              const newTag = {
                name: e.target.value.trim(),
                color: colors[Math.floor(Math.random() * colors.length)],
              };
              onChange({
                field: "tags",
                value: [...taskData.task.tags, newTag],
              });
              e.target.value = "";
              e.preventDefault();
              if (tagInputRef.current) tagInputRef.current.focus();
            } else {
              stopKey(e);
            }
          }}
          mb="md"
          size="lg"
          styles={{
            input: {
              fontSize: "1.1rem",
              padding: "1rem",
            },
          }}
        />

        <Box mb="md" sx={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          {taskData.task.tags.map((tag, idx) => (
            <Box
              key={idx}
              sx={{
                backgroundColor: tag.color,
                padding: "0.5rem 1rem",
                borderRadius: "4px",
                cursor: "pointer",
                fontSize: "1rem",
              }}
              onClick={() => {
                const newTags = taskData.task.tags.filter((_, i) => i !== idx);
                onChange({ field: "tags", value: newTags });
              }}
              title="Click to remove tag"
            >
              {tag.name}
            </Box>
          ))}
        </Box>

        <Group position="right" mt="xl" spacing="md">
          <Button onClick={onSave} color="blue" size="lg">
            Save
          </Button>
          {taskData.mode === "edit" && (
            <Button onClick={onDelete} color="red" size="lg">
              Delete
            </Button>
          )}
          <Button onClick={onClose} color="gray" size="lg">
            Close
          </Button>
        </Group>
      </Box>
    </Drawer>
  );
});

/* ====================
   BOARD MODAL COMPONENT
==================== */
const BoardModal = memo(function BoardModal({
  opened,
  boardTitle,
  boardDescription,
  onChange,
  onSave,
}) {
  return (
    <Modal
      opened={true}
      onClose={onSave}
      title="Edit Board"
      size="lg"
      padding="xl"
      styles={{
        title: {
          fontSize: "1.5rem",
          fontWeight: 600,
        },
        modal: {
          minHeight: "400px",
        },
      }}
    >
      <Box p="md">
        <TextInput
          label="Board Title (optional)"
          placeholder="Enter board title"
          value={boardTitle}
          onChange={(e) =>
            onChange({ field: "boardTitle", value: e.target.value })
          }
          autoFocus
          size="lg"
          mb="lg"
          styles={{
            label: {
              fontSize: "1.1rem",
              marginBottom: "0.5rem",
              fontWeight: 500,
            },
            input: {
              fontSize: "1.1rem",
              padding: "1rem",
              minHeight: "3.5rem",
            },
          }}
        />

        <Textarea
          label="Board Description (optional)"
          placeholder="Enter board description"
          value={boardDescription}
          onChange={(e) =>
            onChange({ field: "boardDescription", value: e.target.value })
          }
          minRows={5}
          size="lg"
          mb="xl"
          styles={{
            label: {
              fontSize: "1.1rem",
              marginBottom: "0.5rem",
              fontWeight: 500,
            },
            input: {
              fontSize: "1.1rem",
              padding: "1rem",
              minHeight: "150px",
            },
          }}
        />

        <Button
          fullWidth
          size="lg"
          onClick={onSave}
          styles={{
            root: {
              height: "3.5rem",
              fontSize: "1.1rem",
            },
          }}
        >
          Save
        </Button>
      </Box>
    </Modal>
  );
});

const ColumnModal = memo(function ColumnModal({
  opened,
  columnName,
  onChange,
  onSave,
  onClose,
}) {
  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Add Column"
      size="lg"
      padding="xl"
      styles={{
        title: { fontSize: "1.5rem", fontWeight: 600 },
        modal: { minHeight: "200px" },
      }}
    >
      <Box p="md">
        <TextInput
          label="Column Name"
          placeholder="Enter column name"
          value={columnName}
          onChange={(e) => onChange(e.target.value)}
          autoFocus
          size="lg"
          mb="xl"
          styles={{
            label: {
              fontSize: "1.1rem",
              marginBottom: "0.5rem",
              fontWeight: 500,
            },
            input: {
              fontSize: "1.1rem",
              padding: "1rem",
              minHeight: "3.5rem",
            },
          }}
        />
        <Button
          fullWidth
          size="lg"
          onClick={onSave}
          styles={{
            root: { height: "3.5rem", fontSize: "1.1rem" },
          }}
        >
          Save
        </Button>
      </Box>
    </Modal>
  );
});

/* ====================
   EDITABLE COLUMN HEADER COMPONENT
==================== */
const EditableColumnHeader = memo(function EditableColumnHeader({
  colId,
  column,
  colors,
  onNameChange,
  onNameBlur,
  onColorChange,
  onDelete,
  dragHandleProps,
}) {
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        overflow: "hidden",
        gap: "8px",
      }}
    >
      <TextInput
        value={column.name}
        onChange={(e) => onNameChange(colId, e.target.value)}
        onBlur={() => onNameBlur(colId)}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") {
            e.target.blur(); // Trigger blur when Enter is pressed
          }
        }}
        placeholder="Column name"
      />

      <div style={{ display: "flex", gap: "4px", flex: "1" }}>
        <Select
          onChange={(value) => onColorChange(colId, value)}
          data={colors.map((color) => ({
            value: color,
            label: (
              <div
                style={{
                  backgroundColor: color,
                  width: "100%",
                  height: "16px",
                }}
              />
            ),
          }))}
          onKeyDown={(e) => e.stopPropagation()}
        />
        <ActionIcon {...dragHandleProps} style={{ cursor: "grab" }}>
          <IconGripVertical size={16} />
        </ActionIcon>
        <ActionIcon color="red" onClick={() => onDelete(colId)}>
          <IconTrash size={16} />
        </ActionIcon>
      </div>
    </div>
  );
});

/* ====================
   KANBAN COMPONENT (MAIN VIEW)
==================== */
const KanbanComponent = ({
  initialData,
  subCategoryId,
  config = {},
  onBoardDeleted,
}) => {
  // Initialize board state from passed-in initialData
  const [columns, setColumns] = useState(initialData.columns);
  const [columnOrder, setColumnOrder] = useState(initialData.columnOrder);
  const [editColumns, setEditColumns] = useState(false);
  const [taskModal, setTaskModal] = useState(null);
  const [showBoardModal, setShowBoardModal] = useState(false);
  const [boardTitle, setBoardTitle] = useState(initialData.boardTitle || "");
  const [boardDescription, setBoardDescription] = useState(
    initialData.boardDescription || ""
  );
  // viewMode: 'kanban' or 'table'
  const [viewMode, setViewMode] = useState("kanban");

  const [showColumnModal, setShowColumnModal] = useState(false);
  const [newColumnName, setNewColumnName] = useState("");

  const { users, isSubmitted } = useStartupStore((state) => ({
    users: state.members,
    isSubmitted: state.isSubmitted
  }));

  const colors = ["#FF6B6B", "#FFE66D", "#45B7D5", "#0251cc", "#ea9713", "#8e4399", "#63ba3c", "#4ECDC4",];

  // --------------------------
  // Update Board Data on Backend
  // --------------------------
  const updateData = async (newColumns, newColumnOrder) => {
    const boardId = initialData.id; // assume board has an id
    const startTime = performance.now();
    try {
      // Construct payload in snake_case to match Rails strong parameters.
      const payload = {
        task_board: {
          board_title: boardTitle,
          board_description: boardDescription,
          columns_attributes: [
            // Include existing columns with their tasks
            ...newColumnOrder.map((colId, index) => {
              const col = newColumns[colId];
              return {
                id: col.id,
                name: col.name,
                color: col.color,
                position: index,
                tasks_attributes: col.tasks.map((task) => ({
                  id: task.id,
                  title: task.title,
                  description: task.description,
                  start_date: task.start_date,
                  end_date: task.end_date,
                  priority: task.priority,
                  // **Include the updated column_id**
                  column_id: col.id,
                  user_ids: task.assignees
                    ? task.assignees.map((assignee) => assignee.id)
                    : [],
                  tags_attributes: task.tags
                    ? task.tags.map((tag) => ({
                        id: tag.id,
                        name: tag.name,
                        color: tag.color,
                      }))
                    : [],
                })),
              };
            }),
            // Also include columns marked for destruction
            ...Object.keys(newColumns)
              .filter(
                (colId) =>
                  newColumns[colId]._destroy && !newColumnOrder.includes(colId)
              )
              .map((colId) => ({
                id: newColumns[colId].id,
                _destroy: true,
              })),
          ],
        },
      };
      
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards/${boardId}`,
        "PUT",
        payload
      );

      const responseTime = Math.round(performance.now() - startTime);

      if (!response.ok) {
        console.error("Failed to update board data");
        Mixpanel.error('Task Board Update Error', {
          error_type: 'API Error',
          component: 'TaskBoard',
          user_action: 'Update Board Data',
          http_status: response.status,
          response_time_ms: responseTime
        });
        return;
      }

      const updatedBoardData = await response.json();

      // Assume your Rails backend now returns columns as an object keyed by column IDs,
      // and a separate columnOrder array.
      const transformedData = {
        id: updatedBoardData.id,
        boardTitle: updatedBoardData.board_title,
        boardDescription: updatedBoardData.board_description,
        columns: updatedBoardData.columns, // object keyed by column id
        columnOrder: updatedBoardData.columnOrder, // array of column ids
      };

      // Update the component's state with the transformed data.
      setColumns(transformedData.columns);
      setColumnOrder(transformedData.columnOrder);
      Mixpanel.settings('Task Board', 'Updated', {
        board_id: boardId,
        response_time_ms: responseTime,
        // tasks_updated: tasksUpdated // Requires deeper comparison
    });
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime);
      Mixpanel.error('Task Board Update Error', {
        error_message: error.message,
        error_type: 'Network Error',
        component: 'TaskBoard',
        user_action: 'Update Board Data',
        board_id: boardId,
        response_time_ms: responseTime
      });
      console.error("Error updating board data:", error);
    }
  };

  // --------------------------
  // TASK HANDLERS
  // --------------------------
  const handleGlobalAddTask = () => {
    setTaskModal({
      mode: "add",
      columnId: "", // user must select column in modal
      task: {
        title: "",
        description: "",
        assignees: [],
        priority: "Medium",
        files: [],
        tags: [],
        start_date: "",
        end_date: "",
      },
    });
  };

  const handleTaskClick = (columnId, index, task) => {
    if(isSubmitted){
      return;
    }
    setTaskModal({
      mode: "edit",
      originalColumnId: columnId,
      columnId,
      index,
      task: {
        ...task,
        files: task.files || [],
        tags: task.tags || [],
        assignees: task.assignees || [],
      },
    });
  };

  const onTaskModalChange = ({ field, value }) => {
    if (field === "columnId") {
      setTaskModal((prev) => ({ ...prev, columnId: value }));
    } else {
      setTaskModal((prev) => ({
        ...prev,
        task: { ...prev.task, [field]: value },
      }));
    }
  };

  const saveNewTask = () => {
    const { columnId, task } = taskModal;
    if (!columnId) return alert("Please select a column for the task.");
    // Ensure the new task has an "assignees" array (even if empty)
    const newTask = { ...task, assignees: task.assignees || [] };
    const newColumns = {
      ...columns,
      [columnId]: {
        ...columns[columnId],
        tasks: [...columns[columnId].tasks, newTask],
      },
    };
    setColumns(newColumns);
    updateData(newColumns, columnOrder);
    setTaskModal(null);
  };

  const saveEditedTask = async () => {
    const { originalColumnId, columnId, index, task } = taskModal;
    // Create a copy of the task with all changes
    const updatedTask = { ...task, assignees: task.assignees || [] };

    console.log(updatedTask);

    // Clone current columns for local state updates
    const newColumns = { ...columns };

    if (originalColumnId === columnId) {
      // If the column hasn't changed, update normally.
      const newTaskList = [...newColumns[columnId].tasks];
      newTaskList[index] = updatedTask;
      newColumns[columnId] = { ...newColumns[columnId], tasks: newTaskList };
      setColumns(newColumns);
      updateData(newColumns, columnOrder);
      setTaskModal(null);
    } else {
      // --- STEP 1: Update non-column fields first ---
      // In this case, we want to update task fields except for the column change.
      // So, we keep the task in the original column temporarily.
      const newTaskList = [...newColumns[originalColumnId].tasks];
      // Force the task's column_id to remain as the original for now.
      newTaskList[index] = { ...updatedTask, column_id: originalColumnId };
      newColumns[originalColumnId] = {
        ...newColumns[originalColumnId],
        tasks: newTaskList,
      };
      setColumns(newColumns);
      updateData(newColumns, columnOrder);
      setTaskModal(null);

      // --- STEP 3: Now call moveTask to update the column change ---
      await moveTask(updatedTask.id, columnId);

      // --- STEP 4: Finally, update local state to reflect the move ---
      // Remove the task from the original column.
      const origTasks = [...newColumns[originalColumnId].tasks];
      const [movedTask] = origTasks.splice(index, 1);
      newColumns[originalColumnId] = {
        ...newColumns[originalColumnId],
        tasks: origTasks,
      };

      // Add the task to the new column.
      // Optionally, update the moved task's column_id to the new value.
      const movedTaskWithNewColumn = { ...movedTask, column_id: columnId };
      const newColumnTasks = [
        ...newColumns[columnId].tasks,
        movedTaskWithNewColumn,
      ];
      newColumns[columnId] = { ...newColumns[columnId], tasks: newColumnTasks };

      setColumns(newColumns);
    }
  };

  const deleteTask = async () => {
    const { columnId, index, task } = taskModal;

    // If the task hasn't been saved to the backend yet, simply update state.
    if (!task.id) {
      const newTaskList = Array.from(columns[columnId].tasks);
      newTaskList.splice(index, 1);
      const newColumns = {
        ...columns,
        [columnId]: { ...columns[columnId], tasks: newTaskList },
      };
      setColumns(newColumns);
      setTaskModal(null);
      return;
    }

    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards/${initialData.id}/columns/${columnId}/tasks/${task.id}`,
        "DELETE"
      );
      if (!response.ok) {
        console.error("Failed to delete task");
        return;
      }
      // On success, remove the task from local state.
      const newTaskList = Array.from(columns[columnId].tasks);
      newTaskList.splice(index, 1);
      const newColumns = {
        ...columns,
        [columnId]: { ...columns[columnId], tasks: newTaskList },
      };
      setColumns(newColumns);
      setTaskModal(null);
    } catch (error) {
      console.error("Error deleting task:", error);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files).map((file) => file.name);
    setTaskModal((prev) => ({
      ...prev,
      task: { ...prev.task, files: [...(prev.task.files || []), ...files] },
    }));
  };

  // --------------------------
  // COLUMN HANDLERS
  // --------------------------
  const handleDeleteColumn = (columnId) => {
    const newColumns = { ...columns };

    if (newColumns[columnId].id) {
      newColumns[columnId] = {
        ...newColumns[columnId],
        _destroy: true,
      };
    } else {
      delete newColumns[columnId];
    }

    const newColumnOrder = columnOrder.filter((id) => id !== columnId);
    setColumns(newColumns);
    setColumnOrder(newColumnOrder);
    updateData(newColumns, newColumnOrder);
  };

  // const handleColumnNameChange = (columnId, newName) => {
  //   const newColumns = {
  //     ...columns,
  //     [columnId]: { ...columns[columnId], name: newName },
  //   };
  //   setColumns(newColumns);
  //   updateData(newColumns, columnOrder);
  // };

  const handleColumnNameChange = (columnId, newName) => {
    // Only updates local state, doesn't call API
    const newColumns = {
      ...columns,
      [columnId]: { ...columns[columnId], name: newName },
    };
    setColumns(newColumns);
  };

  const handleColumnNameBlur = (columnId) => {
    // Only called when user finishes editing
    updateData(columns, columnOrder);
  };

  const handleColumnColorChange = (columnId, color) => {
    const newColumns = {
      ...columns,
      [columnId]: { ...columns[columnId], color },
    };
    setColumns(newColumns);
    updateData(newColumns, columnOrder);
  };

  const handleAddColumn = () => {
    setNewColumnName("");
    setShowColumnModal(true);
  };

  const saveNewColumn = () => {
    if (!newColumnName.trim()) {
      return alert("Column name cannot be empty");
    }
    const newColumnId = `column-${Date.now()}`;
    const newColumns = {
      ...columns,
      [newColumnId]: {
        name: newColumnName.trim(),
        color: colors[0],
        tasks: [],
      },
    };
    const newColumnOrder = [...columnOrder, newColumnId];
    setColumns(newColumns);
    setColumnOrder(newColumnOrder);
    updateData(newColumns, newColumnOrder);
    setShowColumnModal(false);
  };

  const updateTask = async (taskId, columnId, updatedTaskData) => {
    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards/${initialData.id}/columns/${columnId}/tasks/${taskId}`,
        "PATCH",
        {
          task: updatedTaskData,
        }
      );
      if (!response.ok) {
        console.error("Failed to update task");
        return null;
      }
      const result = await response.json();
      return result;
    } catch (error) {
      console.error("Error updating task:", error);
      return null;
    }
  };

  const moveTask = async (taskId, newColumnId) => {
    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards/${initialData.id}/columns/${newColumnId}/tasks/${taskId}/move`,
        "POST"
      );
      if (!response.ok) {
        console.error("Failed to move task");
        return false;
      }
      return true;
    } catch (error) {
      console.error("Error moving task:", error);
      return false;
    }
  };

  // --------------------------
  // DRAG AND DROP HANDLER
  // --------------------------
  const handleDragEnd = async (result) => {
    const { destination, source, draggableId, type } = result;
    if (!destination) return;

    if (type === "COLUMN" && editColumns) {
      const newColumnOrder = Array.from(columnOrder);
      newColumnOrder.splice(source.index, 1);
      newColumnOrder.splice(destination.index, 0, draggableId);
      setColumnOrder(newColumnOrder);
      updateData(columns, newColumnOrder);
      return;
    }

    const startCol = source.droppableId;
    const finishCol = destination.droppableId;
    if (startCol === finishCol) {
      const newTaskList = Array.from(columns[startCol].tasks);
      const [removed] = newTaskList.splice(source.index, 1);
      newTaskList.splice(destination.index, 0, removed);
      const newColumns = {
        ...columns,
        [startCol]: { ...columns[startCol], tasks: newTaskList },
      };
      setColumns(newColumns);
      updateData(newColumns, columnOrder);
    } else {
      // Keep a snapshot for rollback on failure
      const prevColumns = columns;

      const startTasks = Array.from(columns[startCol].tasks);
      const [removed] = startTasks.splice(source.index, 1);
      const finishTasks = Array.from(columns[finishCol].tasks);

      // Optimistically update the task's column_id for stability
      const movedTask = { ...removed, column_id: finishCol };
      finishTasks.splice(destination.index, 0, movedTask);

      const optimisticColumns = {
        ...columns,
        [startCol]: { ...columns[startCol], tasks: startTasks },
        [finishCol]: { ...columns[finishCol], tasks: finishTasks },
      };
      setColumns(optimisticColumns);

      const ok = await moveTask(removed.id, finishCol);
      if (!ok) {
        // Revert on failure
        setColumns(prevColumns);
      }
    }
  };

  const deleteBoard = async () => {
    if (!window.confirm("Are you sure you want to delete this board?")) return;
    try {
      const response = await fetchApi(
        `/sub_categories/${subCategoryId}/task_boards/${initialData.id}`,
        "DELETE"
      );
      if (!response.ok) {
        console.error("Failed to delete board");
        return;
      }
      console.log("Board deleted successfully");
      // If provided, call the parent's callback to update the board list or redirect.
      if (onBoardDeleted) {
        onBoardDeleted(initialData.id);
      }
    } catch (error) {
      console.error("Error deleting board:", error);
    }
  };

  // --------------------------
  // BOARD MODAL HANDLERS
  // --------------------------
  const onBoardModalChange = ({ field, value }) => {
    if (field === "boardTitle") {
      setBoardTitle(value);
    } else {
      setBoardDescription(value);
    }
  };

  // When saving board changes, we update the backend with the new title/description.
  const saveBoard = () => {
    updateData(columns, columnOrder); // update board title/description along with current columns
    setShowBoardModal(false);
  };

  // --------------------------
  // RENDERING
  // --------------------------
  const containerStyle = {};

  const headerButtonStyle = {
    padding: "4px 6px",
    borderRadius: "2px",
    border: "#007bff",
    cursor: "pointer",
    marginRight: "4px",
    fontSize: "12px",
  };

  const columnsContainerStyle = {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
    marginTop: "4px",
  };

  const columnStyle = (color) => ({
    padding: "12px",
    borderRadius: "8px",
    width: "30%",
    overflowX: "scroll",
    borderTop: `4px solid ${color}`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  });

  const taskStyle = {
    padding: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    marginBottom: "8px",
    borderRadius: "4px",
    boxShadow: "0 3px 3px rgba(0,0,0,0.1)",
    cursor: "pointer",
    fontSize: "14px",
  };

  const boardHeaderStyle = {
    marginBottom: "8px",
  };

  return (
    <div style={containerStyle}>
      {showBoardModal && (
        <BoardModal
          boardTitle={boardTitle}
          boardDescription={boardDescription}
          onChange={onBoardModalChange}
          onSave={saveBoard}
        />
      )}

      {showColumnModal && (
        <ColumnModal
          opened={showColumnModal}
          columnName={newColumnName}
          onChange={setNewColumnName}
          onSave={saveNewColumn}
          onClose={() => setShowColumnModal(false)}
        />
      )}

      {taskModal && (
        <TaskModal
          key="task-modal"
          taskData={taskModal}
          onChange={onTaskModalChange}
          onSave={taskModal.mode === "add" ? saveNewTask : saveEditedTask}
          onDelete={deleteTask}
          onClose={() => setTaskModal(null)}
          onFileChange={handleFileChange}
          colors={colors}
          users={users}
          columns={columns}
        />
      )}

      {(boardTitle || boardDescription) && (
        <div style={boardHeaderStyle}>
          {boardTitle && <h3 style={{ margin: "4px 0" }}>{boardTitle}</h3>}
          {boardDescription && (
            <p style={{ margin: "4px 0", color: "#555" }}>{boardDescription}</p>
          )}
        </div>
      )}

      <div
        style={{
          marginRight: "5%",
          justifyContent: "flex-end",
          display: "flex",
        }}
      >
        {!isSubmitted ? (
          <>
            <button onClick={handleAddColumn} style={headerButtonStyle}>
            + Add Column
           </button>
            <button
              onClick={() => setEditColumns(!editColumns)}
              style={headerButtonStyle}
            >
              {editColumns ? "Done Editing Column" : "Edit Columns"}
            </button>
            <button
              onClick={() =>
                setViewMode(viewMode === "kanban" ? "table" : "kanban")
              }
              style={headerButtonStyle}
            >
              {viewMode === "kanban"
                ? "Switch to Table View"
                : "Switch to Board View"}
            </button>
            <button
              onClick={() => setShowBoardModal(true)}
              style={headerButtonStyle}
            >
              Edit Board
            </button>
            <button onClick={deleteBoard} style={headerButtonStyle}>
              Delete Board
            </button>
            <button onClick={handleGlobalAddTask} style={headerButtonStyle}>
              + Add Task
            </button>
          </>
        ): (
          <button
          onClick={() =>
            setViewMode(viewMode === "kanban" ? "table" : "kanban")
          }
          style={headerButtonStyle}
        >
          {viewMode === "kanban"
            ? "Switch to Table View"
            : "Switch to Board View"}
        </button>
        )}

      </div>

      {viewMode === "kanban" ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId="all-columns"
            direction="horizontal"
            type="COLUMN"
          >
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={columnsContainerStyle}
              >
                {columnOrder.map((colId, index) => {
                  const column = columns[colId];
                  return (
                    <Draggable
                      key={colId}
                      draggableId={String(colId)}
                      index={index}
                    >
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            ...columnStyle(column.color),
                            ...provided.draggableProps.style,
                          }}
                        >
                          {editColumns ? (
                            <EditableColumnHeader
                              colId={colId}
                              column={column}
                              colors={colors}
                              onNameChange={handleColumnNameChange}
                              onNameBlur={handleColumnNameBlur}
                              onColorChange={handleColumnColorChange}
                              onDelete={handleDeleteColumn}
                              dragHandleProps={provided.dragHandleProps}
                            />
                          ) : (
                            <div
                              style={{ marginBottom: "8px", fontSize: "14px" }}
                            >
                              {column.name}
                            </div>
                          )}
                          <Droppable droppableId={String(colId)} type="TASK" ignoreContainerClipping={true}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                style={{
                                  minHeight: "200px",
                                  flex: 1,
                                  padding: "4px 2px 60px",
                                  borderRadius: "6px",
                                  transition: "background-color 120ms ease, box-shadow 120ms ease, outline-color 120ms ease",
                                  backgroundColor: snapshot.isDraggingOver ? "rgba(2,81,204,0.06)" : "transparent",
                                  outline: snapshot.isDraggingOver ? `2px dashed ${column.color}` : "2px dashed transparent",
                                }}
                              >
                                {column.tasks.map((task, idx) => (
                                  <Draggable
                                    key={task.id}
                                    draggableId={String(task.id)}
                                    index={idx}
                                    isDragDisabled={isSubmitted}
                                  >
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={{
                                          ...taskStyle,
                                          boxShadow: snapshot.isDragging
                                            ? "0 8px 16px rgba(0,0,0,0.18)"
                                            : "0 3px 3px rgba(0,0,0,0.1)",
                                          backgroundColor: snapshot.isDragging ? "#fff" : undefined,
                                          transition: "box-shadow 120ms ease",
                                          ...provided.draggableProps.style,
                                        }}
                                        onClick={() =>
                                          handleTaskClick(colId, idx, task)
                                        }
                                      >
                                        <h5
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "500",
                                            lineHeight: "1.625",
                                          }}
                                        >
                                          {task.title}
                                        </h5>
                                        <p
                                          style={{
                                            color: "#555",
                                            fontSize: "12px",
                                          }}
                                        >
                                          {task.description.slice(0, 150)}
                                        </p>
                                        {/* {(task.start_date || task.end_date) && (
                                          <div className="mt-2 flex flex-wrap gap-2 text-sm text-gray-500">
                                            {task.start_date && (
                                              <div className="flex items-center gap-1">
                                                <IconClockPlay className="h-4 w-4" />
                                                <span>
                                                  Start: {task.start_date}
                                                </span>
                                              </div>
                                            )}
                                            {task.end_date && (
                                              <div className="flex items-center gap-1">
                                                <IconClockStop className="h-4 w-4" />
                                                <span>
                                                  End: {task.end_date}
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        )} */}

                                        <Group spacing="xs" mt={4}>
                                          {task.assignees &&
                                            task.assignees.map((assignee) => (
                                              <Badge
                                                key={assignee.id}
                                                color="blue"
                                                variant="light"
                                              >
                                                {assignee.first_name}{" "}
                                                {assignee.last_name}
                                              </Badge>
                                            ))}
                                        </Group>

                                        {task.tags?.length > 0 && (
                                          <div className="mt-1 flex flex-wrap gap-1">
                                            {task.tags.map((tag, i) => (
                                              <span
                                                key={i}
                                                className="px-1.5 py-0.5 text-xs rounded"
                                                style={{
                                                  backgroundColor: tag.color,
                                                }}
                                              >
                                                {tag.name}
                                              </span>
                                            ))}
                                          </div>
                                        )}

                                        {task.priority && (
                                          <div
                                            className={`inline-flex items-center gap-1 w-fit rounded text-md font-medium ${
                                              task.priority.toLowerCase() ===
                                              "high"
                                                ? "text-red-600 bg-red-50"
                                                : task.priority.toLowerCase() ===
                                                  "medium"
                                                ? "text-yellow-600 bg-yellow-50"
                                                : "text-blue-600 bg-blue-50"
                                            }`}
                                          >
                                            {task.priority.toLowerCase() ===
                                            "high" ? (
                                              <IconArrowUp className="h-5 w-5" />
                                            ) : task.priority.toLowerCase() ===
                                              "medium" ? (
                                              <IconArrowRight className="h-5 w-5" />
                                            ) : (
                                              <IconArrowDown className="h-5 w-5" />
                                            )}
                                            {task.priority}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <TableView
          columns={columns}
          columnOrder={columnOrder}
          onTaskClick={handleTaskClick}
          onAddTask={handleGlobalAddTask}
          boardTitle={boardTitle}
          boardDescription={boardDescription}
          isSubmitted={isSubmitted}
        />
      )}
    </div>
  );
};

/* ====================
   HELPER: Default Board Data
==================== */
const getDefaultBoardData = () => {
  const columnsAttributes = [
    { name: "To Do", color: "#0251cc", position: 0, tasks_attributes: [] },
    {
      name: "In Progress",
      color: "#ea9713",
      position: 1,
      tasks_attributes: [],
    },
    { name: "Done", color: "#63ba3c", position: 2, tasks_attributes: [] },
  ];
  return {
    board_title: "Task Board Title",
    board_description:
      "Provide a full description covering on how the tasks are to be handle.",
    columns_attributes: columnsAttributes,
  };
};

/* ====================
   TASK BOARDS MANAGER
==================== */
// This parent component lets users create new boards via the button.
const TaskBoardsManager = ({ subCategoryId, toggle, name }) => {
  const [boards, setBoards] = useState([]);
  const [loading, setLoading] = useState(true);
  const {isSubmitted} = useStartupStore((state)=> ({
    isSubmitted: state.isSubmitted
  }))
  // Fetch boards from the API whenever subCategoryId changes.
  useEffect(() => {
    if (subCategoryId) {
      fetchApi(`/sub_categories/${subCategoryId}/task_boards`, "GET")
        .then(async (response) => {
          if (!response.ok) {
            throw new Error("Error fetching task boards");
          }
          const data = await response.json();
          console.log(data);
          if (data.length === 0) {
            // handleCreateNewBoard();
          } else {
            setBoards(data);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching boards:", error);
          setLoading(false);
        });
    }
  }, [subCategoryId]);

  // Handle creating a new board by POSTing to the backend.
  const handleCreateNewBoard = () => {
    const newBoardData = getDefaultBoardData();
    // Rails expects the board data under a `task_board` key.
    fetchApi(`/sub_categories/${subCategoryId}/task_boards`, "POST", {
      task_board: newBoardData,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Error creating board");
        }
        const data = await response.json();
        setBoards([...boards, data]);
      })
      .catch((error) => console.error("Error creating board:", error));
  };

  const handleBoardDeleted = (deletedBoardId) => {
    setBoards(boards.filter((board) => board.id !== deletedBoardId));
  };

  if (loading) {
    return <div>Loading boards...</div>;
  }

  const headerButtonStyle = {
    paddingRight: "8px",
    borderRadius: "2px",
    border: "#007bff",
    cursor: "pointer",
    marginRight: "4px",
    fontSize: "12px",
  };

  return (
    <div style={{ paddingInline: "20px", width: "100%" }}>
      <Title order={3} color="dimmed" mb={20}>
        {name} Task board
      </Title>

      <div
        style={{
          justifyContent: "right",
          display: "flex",
          marginBottom: "8px",
        }}
      >
      {!isSubmitted && (
          <button onClick={handleCreateNewBoard} style={headerButtonStyle}>
           + Create new task board
         </button>
      )} 
        <button onClick={() => toggle(false)} style={headerButtonStyle}>
          Switch back to editor
        </button>
      </div>
      {boards.map((board) => (
        <Box
          key={board.id}
          sx={{
            border: "1px solid #ddd",
            padding: "10px",
            marginBottom: "20px",
            borderRadius: "4px",
          }}
        >
          <KanbanComponent
            subCategoryId={subCategoryId}
            initialData={board}
            onBoardDeleted={handleBoardDeleted}
          />
        </Box>
      ))}
    </div>
  );
};

export default TaskBoardsManager;
